import {
  PriceAvailabilityResponse,
  PriceAvailabilityResponseHeadless,
  ProductPriceAvailabilityHeadless
} from 'services/priceAvailability/types';
import {
  DealerPriceAvailabilityResponse,
  ProductDealerPriceAvailability
} from './types';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { defaultItem } from './constants';

const getPriceValue = (value: string, validationValue: string) => {
  const isValidNumber = /^[+-]?\d+(\.\d+)?$/.test(validationValue);
  const isValidValue = isValidNumber ? parseFloat(validationValue) > 0 : false;

  if (isEmpty(validationValue) || !isValidValue) {
    return '';
  }

  return value;
};

const transformPriceItem = (item: ProductDealerPriceAvailability) => {
  return {
    ...item,
    price: {
      unitPrice: getPriceValue(item.unitPrice, item.unformattedUnitPrice),
      discountedPrice: item.discountedPrice,
      discountAmount: item.discountedAmount,
      coreCharges: getPriceValue(item.coreCharges, item.unformattedCoreCharges),
      totalPrice: getPriceValue(item.totalPrice, item.unformattedTotalPrice)
    },
    availability: item.availability?.map(
      ({ availabilityMessage, quantityAvailable, showContactDealerPopUp }) => ({
        availabilityMessage: availabilityMessage,
        quantityAvailable: Number(quantityAvailable),
        showContactDealerPopUp
      })
    ),
    partLevelFlags: {
      isNonReturnable: item.nonReturnable,
      isCatPart: item.isCatPart,
      isIndirectReplacement: item.indirectReplacement,
      isAlternatePartsExist: item.alternatePartsExist,
      isAddToCartEnabled: item.enableAddToCart,
      isRemanPart: item.reman,
      isHoseAssemblyPart: item.hoseAssemblyIndicator
    },
    weight: !isEmpty(item.weight) ? +item.weight : null
  };
};

export const transformPriceAndAvailabilityHeadless = (
  priceData: PriceAvailabilityResponseHeadless,
  partNumber?: string
): PriceAvailabilityResponse => {
  const productPriceAvailability = priceData?.items?.find(
    (data) => data.partNumber === partNumber
  );
  if (partNumber) {
    return priceData && productPriceAvailability
      ? {
          isGstAvailable: priceData.isGstAvailable,
          isGstIncludesTax: priceData.isGstIncludesTax,
          currency: priceData.currency,
          item: {
            ...productPriceAvailability,
            price: {
              unitPrice: getPriceValue(
                '' +
                  productPriceAvailability?.price.formatted.undiscountedPrice,
                '' +
                  productPriceAvailability?.price.unformatted.undiscountedPrice
              ),
              discountedPrice:
                productPriceAvailability?.price.unformatted.discountedPrice,
              discountAmount:
                productPriceAvailability?.price.unformatted.discountAmount.toString(),
              coreCharges: getPriceValue(
                productPriceAvailability?.price.formatted.coreCharges,
                '' + productPriceAvailability?.price.unformatted.coreCharges
              ),
              totalPrice: getPriceValue(
                productPriceAvailability?.price.formatted.totalPrice,
                '' + productPriceAvailability?.price.unformatted.totalPrice
              ),
              unformattedTotalPrice:
                productPriceAvailability?.price.unformatted.totalPrice
            },
            replacementParts: productPriceAvailability?.replacementParts,
            availability: productPriceAvailability?.availability?.map(
              ({
                availabilityMessage,
                quantityAvailable,
                showDealerContactInformation
              }) => ({
                availabilityMessage: availabilityMessage,
                quantityAvailable: Number(quantityAvailable),
                showContactDealerPopUp: showDealerContactInformation
              })
            )
          }
        }
      : {
          error: true,
          currency: null,
          isGstAvailable: null,
          isGstIncludesTax: null,
          item: {
            price: {
              totalPrice: '',
              coreCharges: '',
              unformattedTotalPrice: 0,
              unitPrice: '',
              discountAmount: '',
              discountedPrice: 0
            }
          }
        };
  } else {
    return priceData?.items && priceData?.items.length > 0
      ? {
          isGstAvailable: priceData.isGstAvailable,
          isGstIncludesTax: priceData.isGstIncludesTax,
          currency: priceData.currency,
          error: false,
          items: priceData.items.map((item) => transformPriceItemHeadless(item))
        }
      : {
          error: true,
          currency: null,
          isGstAvailable: null,
          isGstIncludesTax: null,
          items: [
            {
              price: {
                totalPrice: '',
                coreCharges: '',
                unformattedTotalPrice: 0,
                unitPrice: '',
                discountAmount: '',
                discountedPrice: 0
              }
            }
          ]
        };
  }
};
export const transformPriceItemHeadless = (
  item: ProductPriceAvailabilityHeadless
) => {
  return {
    ...item,
    storeAvailability: item.sourcingDetails.storeAvailability,
    unitPrice: getPriceValue(
      '' + item?.price?.formatted?.undiscountedPrice,
      '' + item?.price?.unformatted?.undiscountedPrice
    ),
    discountedPrice: item?.price?.unformatted?.discountedPrice,
    discountAmount: item?.price?.unformatted?.discountAmount.toString(),
    coreCharges: getPriceValue(
      item?.price?.formatted?.coreCharges,
      '' + item?.price?.unformatted?.coreCharges
    ),
    totalPrice: getPriceValue(
      item?.price?.formatted?.totalPrice,
      '' + item?.price?.unformatted?.totalPrice
    ),
    unformattedTotalPrice: item?.price?.unformatted?.totalPrice,
    price: {
      unitPrice: getPriceValue(
        '' + item?.price?.formatted?.undiscountedPrice,
        '' + item?.price?.unformatted?.undiscountedPrice
      ),
      discountedPrice: item?.price?.unformatted?.discountedPrice,
      discountAmount: item?.price?.unformatted?.discountAmount.toString(),
      coreCharges: getPriceValue(
        item?.price?.formatted?.coreCharges,
        '' + item?.price?.unformatted?.coreCharges
      ),
      totalPrice: getPriceValue(
        item?.price?.formatted?.totalPrice,
        '' + item?.price?.unformatted?.totalPrice
      ),
      unformattedTotalPrice: item?.price?.unformatted?.totalPrice
    },
    sourcingDetails: item.sourcingDetails.availabilityBySource,
    nonReturnable: item.partLevelFlags.isNonReturnable,
    enableAddToCart: item.partLevelFlags.isAddToCartEnabled,
    availability: item.availability?.map(
      ({
        availabilityMessage,
        quantityAvailable,
        showDealerContactInformation
      }) => ({
        availabilityMessage: availabilityMessage,
        quantityAvailable: Number(quantityAvailable),
        showContactDealerPopUp: showDealerContactInformation
      })
    )
  };
};
export const transformPriceAndAvailability = (
  priceData: DealerPriceAvailabilityResponse,
  partNumber?: string
): PriceAvailabilityResponse => {
  const { priceAndAvailability, ...rest } = priceData;

  if (partNumber) {
    const productPriceAvailability = priceData?.priceAndAvailability?.find(
      (data) => data.partNumber === partNumber
    );
    if (!isEmpty(productPriceAvailability)) {
      return {
        ...rest,
        item: transformPriceItem(productPriceAvailability)
      };
    }

    return {
      ...rest,
      item: defaultItem
    };
  }

  return {
    ...rest,
    items: priceAndAvailability?.map((item) => transformPriceItem(item))
  };
};

export const transformPriceAndAvailabilityHeadlessData = (
  priceData,
  partNumber
) => {
  const productPriceAvailability = priceData?.items?.find(
    (data) => data.partNumber === partNumber
  );

  return {
    isGstAvailable: priceData.isGstAvailable,
    isGstIncludesTax: priceData.isGstIncludesTax,
    currency: priceData.currency,
    item: {
      ...productPriceAvailability,
      price: {
        unitPrice: productPriceAvailability.price.unitPrice,
        discountedPrice: productPriceAvailability.price.discountedPrice,
        discountAmount:
          productPriceAvailability.price.discountAmount.toString(),
        coreCharges: productPriceAvailability.price.coreCharges,
        totalPrice: productPriceAvailability.price.totalPrice,
        unformattedTotalPrice: productPriceAvailability.price.totalPrice
      },
      replacementParts: productPriceAvailability.replacementParts,
      availability: productPriceAvailability.availability?.map(
        ({
          availabilityMessage,
          quantityAvailable,
          showDealerContactInformation
        }) => ({
          availabilityMessage: availabilityMessage,
          quantityAvailable: Number(quantityAvailable),
          showContactDealerPopUp: showDealerContactInformation
        })
      )
    }
  };
};

const mapAvailability = (availability) => {
  if (!Array.isArray(availability)) return [];
  return availability.map((a) => ({
    availabilityMessage: a.availabilityMessage,
    availableByDate: a.availableByDate,
    quantityAvailable: a.quantityAvailable?.toString() ?? '',
    showContactDealerPopUp: a.showDealerContactInformation ?? false
  }));
};

const mapSourcingDetails = (availabilityBySource) => {
  if (!Array.isArray(availabilityBySource)) return [];
  return availabilityBySource.map((s) => ({
    availabilityMessage: s.availabilityMessage ?? '',
    availableByDate: s.availableByDate,
    sequence: s.sequence,
    availStoreList: Array.isArray(s.storesWithAvailability)
      ? s.storesWithAvailability.map((store) => ({
          quantityAvailable: store.quantityAvailable?.toString() ?? '',
          storeName: store.storeName,
          leadTimeDateRange: store.leadTimeDateRange
        }))
      : []
  }));
};

const getStoreAvailabilityByDate = (availability) => {
  if (Array.isArray(availability) && availability.length > 0) {
    return [
      {
        date: availability[0].availableByDate,
        quantity: Number(availability[0].quantityAvailable)
      }
    ];
  }
  return undefined;
};

const getFormattedOrDefault = (obj, field, def = '') => obj?.[field] ?? def;
const getUnformattedOrDefault = (obj, field, def = '') =>
  obj?.[field]?.toString() ?? def;
const getFlagOrDefault = (flags, field) => flags?.[field] ?? false;

const mapReplacementPart = (part) => {
  const priceFormatted = part.price?.formatted || {};
  const priceUnformatted = part.price?.unformatted || {};
  const partFlags = part.partLevelFlags || {};
  return {
    length: part.length,
    replacementMessage: part.replacementMessage,
    totalPrice: getFormattedOrDefault(priceFormatted, 'totalPrice'),
    pdpURL: '',
    storeAvailability: {
      byDate: getStoreAvailabilityByDate(part.availability),
      contactDealer: null,
      inStock: null
    },
    description: part.partName,
    availability: mapAvailability(part.availability),
    catEntryId: part.id,
    discountedAmount: getFormattedOrDefault(priceFormatted, 'discountAmount'),
    unformattedCoreCharges: getUnformattedOrDefault(
      priceUnformatted,
      'coreCharges'
    ),
    sourcingDetails: mapSourcingDetails(part.availabilityBySource),
    nonReturnable: getFlagOrDefault(partFlags, 'isNonReturnable'),
    imageURL: '',
    isCatPart: getFlagOrDefault(partFlags, 'isCatPart'),
    unitPrice: getFormattedOrDefault(priceFormatted, 'unitPrice'),
    quantity: part.quantity,
    categoryPath: '',
    enableAddToCart: getFlagOrDefault(partFlags, 'isAddToCartEnabled'),
    weight: part.weight,
    parentCatEntryId: part.id,
    alternatePartsExist: getFlagOrDefault(partFlags, 'isAlternatePartsExist'),
    deliveryAvailability: { byDate: [] },
    sosCode: part.sosCode,
    reman: getFlagOrDefault(partFlags, 'isRemanPart'),
    discountedPrice: getFormattedOrDefault(priceFormatted, 'discountedPrice'),
    name: part.partName,
    partNumber: part.partNumber,
    unformattedUnitPrice: getUnformattedOrDefault(
      priceUnformatted,
      'unitPrice'
    ),
    itemCatEntryId: part.id,
    unformattedTotalPrice: getUnformattedOrDefault(
      priceUnformatted,
      'totalPrice'
    ),
    uniqueId: '',
    coreCharges: getFormattedOrDefault(priceFormatted, 'coreCharges')
  };
};

export const convertReplacementPartsToOldFormat = (parts) => {
  if (!Array.isArray(parts)) return [];
  return parts.map(mapReplacementPart);
};

const getFormattedPriceField = (formatted, field) =>
  formatted?.[field]?.toString() ?? '';
const getUnformattedPriceField = (unformatted, field) =>
  unformatted?.[field] !== undefined ? String(unformatted[field]) : '';
const getPartFlag = (flags, field) => flags?.[field] ?? false;

const mapDealerPriceAvailabilityItem = (item) => {
  const formatted = item.price?.formatted;
  const unformatted = item.price?.unformatted;
  const flags = item.partLevelFlags;
  return {
    partNumber: item.partNumber ?? '',
    partName: item.partName ?? '',
    quantity: item.quantity ?? 0,
    unitPrice: getFormattedPriceField(formatted, 'unitPrice'),
    discountedPrice: getFormattedPriceField(formatted, 'discountedPrice'),
    discountAmount: getFormattedPriceField(formatted, 'discountAmount'),
    coreCharges: getUnformattedPriceField(unformatted, 'coreCharges'),
    totalPrice: getFormattedPriceField(formatted, 'totalPrice'),
    formattedCoreCharges: getFormattedPriceField(formatted, 'coreCharges'),
    unformattedTotalPrice: getUnformattedPriceField(unformatted, 'totalPrice'),
    unformattedUnitPrice: getUnformattedPriceField(unformatted, 'unitPrice'),
    weight: item.weight ? String(item.weight) : '',
    replacementMessage: item.replacementMessage ?? '',
    sosCode: item.sosCode ?? '',
    nonReturnable: getPartFlag(flags, 'isNonReturnable'),
    isCatPart: getPartFlag(flags, 'isCatPart'),
    enableAddToCart: getPartFlag(flags, 'isAddToCartEnabled'),
    indirectReplacement: getPartFlag(flags, 'isIndirectReplacement'),
    alternatePartsExist: getPartFlag(flags, 'isAlternatePartsExist'),
    reman: getPartFlag(flags, 'isRemanPart'),
    hoseAssemblyIndicator: item.hoseAssemblyIndicator ?? false,
    replacementParts: Array.isArray(item.replacementParts)
      ? convertReplacementPartsToOldFormat(item.replacementParts)
      : [],
    storeAvailability: item.sourcingDetails?.storeAvailability ?? {},
    availability: mapAvailabilityForDealer(item.availability),
    sourcingDetails: item.sourcingDetails?.availabilityBySource ?? []
  };
};

const mapAvailabilityForDealer = (availability) => {
  if (!Array.isArray(availability)) return [];
  return availability.map((avail) => ({
    availabilityMessage: avail.availabilityMessage ?? '',
    availableByDate: avail.availableByDate ?? '',
    quantityAvailable: String(avail.quantityAvailable ?? ''),
    showContactDealerPopUp: avail.showDealerContactInformation ?? false
  }));
};

export const transformHeadlessToDealerPriceAvailability = (
  headless: PriceAvailabilityResponseHeadless
): DealerPriceAvailabilityResponse => {
  return {
    bundleTotal: '',
    currency: headless.currency ?? '',
    displayCheckPrice: false,
    enableAddAllToCart: false,
    formattedZeroPrice: '',
    isGstIncludesTax: headless.isGstIncludesTax ?? false,
    isGstAvailable: headless.isGstAvailable ?? false,
    resourceName: '',
    showPriceAndAvailabilityFlag: true,
    taxDisclaimerMessage: '',
    taxDisclaimerMessageFooter: '',
    priceAndAvailability: (headless.items ?? []).map(
      mapDealerPriceAvailabilityItem
    )
  };
};
